import React, { useEffect, useState } from 'react';

// Free Google Translate Website Widget — NO API key required.
const LANGS = [
  { code: 'en',    short: 'EN', label: 'English',  flag: '🇬🇧' },
  { code: 'ar',    short: 'AR', label: 'العربية',   flag: '🇸🇦' },
  { code: 'fr',    short: 'FR', label: 'Français', flag: '🇫🇷' },
  { code: 'es',    short: 'ES', label: 'Español',  flag: '🇪🇸' },
  { code: 'de',    short: 'DE', label: 'Deutsch',  flag: '🇩🇪' },
  { code: 'zh-CN', short: 'ZH', label: '中文',      flag: '🇨🇳' },
  { code: 'hi',    short: 'HI', label: 'हिन्दी',      flag: '🇮🇳' },
  { code: 'ur',    short: 'UR', label: 'اردو',     flag: '🇵🇰' },
  { code: 'tr',    short: 'TR', label: 'Türkçe',   flag: '🇹🇷' },
  { code: 'ru',    short: 'RU', label: 'Русский',  flag: '🇷🇺' },
  { code: 'ja',    short: 'JA', label: '日本語',    flag: '🇯🇵' },
];

function _domainVariants() {
  const host = window.location.hostname;
  const variants = ['', host];                 // host-only + bare
  if (host && !host.includes('localhost')) {
    variants.push('.' + host);                 // dotted host
    const parts = host.split('.');
    // include parent domain (e.g. .emergentagent.com) — Google Translate
    // often writes the cookie at the eTLD+1 level.
    if (parts.length > 2) variants.push('.' + parts.slice(-2).join('.'));
    if (parts.length > 3) variants.push('.' + parts.slice(-3).join('.'));
  }
  return variants;
}

function setCookie(name, val, days = 365) {
  const d = new Date(); d.setTime(d.getTime() + days * 86400000);
  for (const dom of _domainVariants()) {
    const domAttr = dom ? `;domain=${dom}` : '';
    document.cookie = `${name}=${val};expires=${d.toUTCString()};path=/${domAttr}`;
  }
}

function clearCookie(name) {
  // Expire in the past across every path + domain variant Google Translate may have written to.
  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  for (const dom of _domainVariants()) {
    const domAttr = dom ? `;domain=${dom}` : '';
    document.cookie = `${name}=;expires=${past};path=/${domAttr}`;
    document.cookie = `${name}=;expires=${past};path=/;${domAttr}`;
  }
}

export default function LanguageSwitcher({ dark = false }) {
  const [current, setCurrent] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('ve_lang')) || 'en');
  const [open, setOpen] = useState(false);
  // `dark` prop is now optional/legacy — we drive colours off the global
  // .dark class on <html> via Tailwind so this works in BOTH the public
  // Splash header AND the authenticated dashboard top bar without anyone
  // having to remember to pass the prop.

  useEffect(() => {
    // Source of truth: localStorage. The googtrans cookie is best-effort —
    // browsers/CDNs sometimes preserve it at a parent domain after a logout,
    // which makes the widget re-translate on every reload. We re-enforce
    // the user's stored preference on every mount.
    const stored = localStorage.getItem('ve_lang') || 'en';
    if (stored === 'en') {
      // Make sure no stale googtrans cookie is left behind.
      const stale = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
      if (stale && stale[1] !== 'en') {
        clearCookie('googtrans');
        clearCookie('googtrans-by-page');
        // Reload once to flush the widget if a stale cookie was found.
        window.location.reload();
        return;
      }
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'en');
    } else {
      document.documentElement.setAttribute('dir', (stored === 'ar' || stored === 'ur') ? 'rtl' : 'ltr');
      document.documentElement.setAttribute('lang', stored);
    }

    // Inject Google Translate widget if not already
    if (!window.__veGTranslateInjected) {
      window.__veGTranslateInjected = true;
      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement({
          pageLanguage: 'en',
          includedLanguages: LANGS.map((l) => l.code).join(','),
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        }, 've-gtranslate-host');
      };
      const s = document.createElement('script');
      s.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      s.async = true;
      document.body.appendChild(s);

      // Style hide Google's branding banner
      const css = document.createElement('style');
      css.innerHTML = `
        .goog-te-banner-frame, .skiptranslate { display:none !important; }
        body { top:0 !important; }
        #ve-gtranslate-host { display:none; }
      `;
      document.head.appendChild(css);
    }
  }, []);

  const setLang = (code) => {
    setCurrent(code);
    setOpen(false);
    // Persist the user's choice as the source of truth.
    localStorage.setItem('ve_lang', code);
    if (code === 'en') {
      // Properly DELETE the googtrans cookie across all domain+path variants.
      // An empty value or single-domain delete leaves the widget sticky on the
      // last non-English language. We also clear Google Translate's own
      // internal cookies that decide the active language on next reload.
      clearCookie('googtrans');
      clearCookie('googtrans-by-page');
    } else {
      setCookie('googtrans', `/en/${code}`);
    }
    // Apply RTL for Arabic / Urdu
    document.documentElement.setAttribute('dir', (code === 'ar' || code === 'ur') ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', code);
    // Reload so the widget picks up the cookie state
    window.location.reload();
  };

  const currentEntry = LANGS.find((l) => l.code === current) || LANGS[0];
  const currentLabel = currentEntry.label;
  const currentFlag = currentEntry.flag;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="lang-switcher"
        title={`Language: ${currentLabel}`}
        aria-label={`Language: ${currentLabel}`}
        className={`h-9 px-2.5 rounded-md border border-slate-200/70 dark:border-white/10
                    bg-white/70 dark:bg-white/[0.03] hover:border-[#8B3FC5] transition-colors
                    inline-flex items-center gap-1.5
                    ${dark ? 'text-white/85 hover:text-[#8B3FC5]' : ''}`}
      >
        {/* Both the flag and the short-code use the SAME font-size + same
            line-height + same vertical-align so they share a baseline.
            Without this, the emoji rendered at text-base + the letters at
            text-[10.5px] caused a visible vertical jitter ("up and down"). */}
        <span
          className="notranslate text-[13px] leading-[1] inline-block translate-y-[0.5px]"
          aria-hidden
        >
          {currentFlag}
        </span>
        <span className="notranslate text-[11px] leading-[1] font-bold tracking-[0.14em] text-slate-700 dark:text-white/85">
          {currentEntry.short}
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute right-0 top-full mt-2 z-50 ve-card p-2 min-w-[180px] bg-white dark:bg-[#141414] shadow-2xl border border-slate-200 dark:border-white/10">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`lang-${l.code}`}
                className={`w-full text-left px-3 py-1.5 text-xs rounded notranslate transition-colors inline-flex items-center gap-2 ${
                  current === l.code
                    ? 'bg-[#8B3FC5]/15 text-[#5E1F8B] dark:text-[#8B3FC5] font-bold'
                    : 'text-slate-700 hover:bg-slate-50 dark:text-white/80 dark:hover:bg-white/[0.06]'
                }`}
              >
                <span
                  aria-hidden
                  className="text-[13px] leading-[1] inline-block translate-y-[0.5px] w-5 text-center"
                >
                  {l.flag}
                </span>
                <span className="flex-1 text-xs leading-[1]">{l.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
      <div id="ve-gtranslate-host" style={{ display: 'none' }}/>
    </div>
  );
}
